import {
  EMPTY_BOARD,
  WIN_LINES,
  type Board,
  type CellIndex,
  type GameState,
  type Mark,
} from './types';

type Cell = Board[number];

function opposite(mark: Mark): Mark {
  return mark === 'X' ? 'O' : 'X';
}

function isCellIndex(value: number): value is CellIndex {
  return Number.isInteger(value) && value >= 0 && value <= 8;
}

export function findWinner(board: Board): { winner: Mark; line: readonly CellIndex[] } | null {
  for (const line of WIN_LINES) {
    const a = line[0];
    const b = line[1];
    const c = line[2];
    if (a === undefined || b === undefined || c === undefined) continue;
    const mark = board[a];
    if (mark && mark === board[b] && mark === board[c]) {
      return { winner: mark, line };
    }
  }
  return null;
}

export function createEmptyState(input: {
  id: string;
  nameX: string;
  nameO?: string;
  starter?: Mark;
}): GameState {
  const starter = input.starter ?? 'X';
  return {
    id: input.id,
    nameX: input.nameX.trim(),
    nameO: (input.nameO ?? '').trim(),
    starter,
    moves: [],
    board: EMPTY_BOARD,
    turn: starter,
    status: 'playing',
    winner: null,
    winningLine: null,
  };
}

export function rebuildFromMoves(input: {
  id: string;
  nameX: string;
  nameO: string;
  starter: Mark;
  moves: readonly number[];
}): GameState {
  const board: [Cell, Cell, Cell, Cell, Cell, Cell, Cell, Cell, Cell] = [
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
    null,
  ];
  let turn: Mark = input.starter;
  const applied: CellIndex[] = [];

  for (const raw of input.moves) {
    if (!isCellIndex(raw)) {
      throw new Error('invalid_move_index');
    }
    if (board[raw] !== null) {
      throw new Error('cell_occupied');
    }
    if (findWinner(board) || applied.length === 9) {
      throw new Error('move_after_end');
    }

    board[raw] = turn;
    applied.push(raw);
    turn = opposite(turn);
  }

  const finalBoard: Board = board;
  const win = findWinner(finalBoard);
  const status = win ? 'won' : applied.length === 9 ? 'draw' : 'playing';

  return {
    id: input.id,
    nameX: input.nameX.trim(),
    nameO: input.nameO.trim(),
    starter: input.starter,
    moves: applied,
    board: finalBoard,
    turn,
    status,
    winner: win?.winner ?? null,
    winningLine: win?.line ?? null,
  };
}

export function applyMove(state: GameState, cell: CellIndex): GameState {
  if (state.status !== 'playing') {
    throw new Error('game_over');
  }
  if (state.board[cell] !== null) {
    throw new Error('cell_occupied');
  }

  return rebuildFromMoves({
    id: state.id,
    nameX: state.nameX,
    nameO: state.nameO,
    starter: state.starter,
    moves: [...state.moves, cell],
  });
}

export function withGuestName(state: GameState, nameO: string): GameState {
  const trimmed = nameO.trim();
  if (!trimmed) {
    throw new Error('empty_name');
  }
  return { ...state, nameO: trimmed };
}

export function createRematch(state: GameState, newId: string): GameState {
  if (!state.nameX || !state.nameO) {
    throw new Error('names_required');
  }
  return createEmptyState({
    id: newId,
    nameX: state.nameX,
    nameO: state.nameO,
    starter: opposite(state.starter),
  });
}

export function generateGameId(): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}
